/** "What bots remember" inside QuantisAI: where a member (who has no bot
 * settings screen) sees, reads and deletes the notes bots kept while working
 * for them, and reads the workspace notes. Hidden while loading, when the
 * feature is off, or when loading fails. */
import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../shared/ui/sheet';
import { BotMemoryScopeView } from '../workspaces/bots/BotMemoryScopeView';
import { scopeLine } from '../workspaces/bots/botMemoryFormat';
import { useBotMemorySummary } from '../workspaces/bots/useBotMemory';

export function SIBotMemory({ workspaceId }: { workspaceId: string }) {
  const { summary, loaded, refresh } = useBotMemorySummary(workspaceId);
  const [open, setOpen] = useState(false);
  if (!loaded || !summary?.enabled) return null;

  return (
    <section aria-label="What bots remember" className="mb-4">
      <button type="button" onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-md border border-separator px-3 py-2 text-left">
        <span className="min-w-0 flex-1 text-xs font-medium text-muted-foreground">What bots remember</span>
        <span className="text-xs text-muted-foreground">{scopeLine(summary.me)}</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="h-[100dvh] w-full gap-0 sm:max-w-none lg:w-[480px] lg:max-w-[480px]">
          <SheetHeader className="pr-12">
            <SheetTitle>What bots remember</SheetTitle>
            <SheetDescription>
              Notes bots keep while working for you, so they do not start from scratch each time. All bots in this workspace share them when they work for you. Workspace admins can see these notes. Earlier versions of a note a bot has rewritten are only erased by Clear all.
            </SheetDescription>
          </SheetHeader>
          {open && (
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-6 font-apple">
              <section className="space-y-2" aria-labelledby="bot-memory-mine">
                <h3 id="bot-memory-mine" className="text-sm font-medium">Your notes</h3>
                <BotMemoryScopeView workspaceId={workspaceId} scope="me" owner="you" canDelete oversized={summary.me.oversized}
                  emptyText="Bots have not kept any notes about working with you yet." onChanged={refresh} />
              </section>
              {summary.workspace.count > 0 && (
                <section className="space-y-2" aria-labelledby="bot-memory-workspace">
                  <div>
                    <h3 id="bot-memory-workspace" className="text-sm font-medium">Workspace notes</h3>
                    <p className="text-xs text-muted-foreground">Written by this workspace's admins. Every bot reads them and none can change them.</p>
                  </div>
                  <BotMemoryScopeView workspaceId={workspaceId} scope="workspace" owner="workspace" canDelete={false}
                    emptyText="No workspace notes." />
                </section>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
